import { t } from '@exam-prep/i18n';
import type { MockSummary } from '../../../lib/api-types';

/** One block of the Mock library: a heading (Tier 1, Tier 2…) or none when there is one stage. */
export interface LibraryGroup<M> {
  stageKey: string;
  heading: string | null;
  mocks: M[];
}

/** `tier1` → "Tier 1", `paper2` → "Paper 2"; any other stage key is shown as it is. */
export function stageHeading(stageKey: string): string {
  const match = /^(tier|paper)(\d+)$/.exec(stageKey);
  if (!match) return stageKey;
  return t(match[1] === 'tier' ? 'miniapp.hub.stage_tier' : 'miniapp.hub.stage_paper', {
    n: match[2] ?? '',
  });
}

/**
 * The library split by stage, so a CGL or CHSL learner sees "Tier 1" and "Tier 2" mocks under
 * their own headings. Stages appear in tier order (tier1 before tier2), mocks keep the server's
 * order inside each. One stage only: a single group with no heading.
 */
export function libraryGroups<M extends Pick<MockSummary, 'stageKey'>>(
  library: readonly M[],
): LibraryGroup<M>[] {
  const byStage = new Map<string, M[]>();
  for (const mock of library) {
    const list = byStage.get(mock.stageKey);
    if (list) list.push(mock);
    else byStage.set(mock.stageKey, [mock]);
  }
  const keys = [...byStage.keys()].sort((one, two) =>
    one.localeCompare(two, 'en', { numeric: true }),
  );
  const single = keys.length <= 1;
  return keys.map((stageKey) => ({
    stageKey,
    heading: single ? null : stageHeading(stageKey),
    mocks: byStage.get(stageKey) ?? [],
  }));
}
